import _ from 'lodash';
import axios from 'axios';
// Use different DOMParser based on browser/nodejs implementation
import DomParser from 'DOMParser';
import { PANZOOM } from './constants.js';

const k = 1024;
const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

/**
 * Converts the given html to bbcode format (for godot)
 * @param {string} html The html string to convert
 * @param {string} newLine The new line character to be used
 * @returns {string} The same string in bbcode format (for godot)
 */
function convertHTMLtoBBCode(str, newLine = '\n') {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');

  // Replace emphasis (italics) with the [i] bbcode tag for godot
  doc.querySelectorAll('em').forEach(emphasis => {
    emphasis.replaceWith(`[i]${emphasis.innerHTML}[/i]`);
  });
  // Replace strong (bold) with the [b] bbcode tag for godot
  doc.querySelectorAll('strong').forEach(strong => {
    strong.replaceWith(`[b]${strong.innerHTML}[/b]`);
  });
  // Replace underline with the [u] bbcode tag for godot
  doc.querySelectorAll('u').forEach(underline => {
    underline.replaceWith(`[u]${underline.innerHTML}[/u]`);
  });
  // Replace mentions with the inner text
  doc.querySelectorAll('span').forEach(el => {
    el.replaceWith(el.innerHTML);
  });
  // Replace urls with the [url] bbcode tag for godot
  doc.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href');
    const text = anchor.innerHTML;
    anchor.replaceWith(`[url=${href}]${text}[/url]`);
  });
  // Replace blockquotes with the [quote] bbcode tag (not yet supported in godot)
  doc.querySelectorAll('blockquote').forEach(blockquote => {
    blockquote.replaceWith(`[quote]${blockquote.innerHTML}[/quote]`);
  });
  // Replace all paragraphs with a newline \n
  doc.querySelectorAll('p').forEach(paragraph => {
    paragraph.replaceWith(`${paragraph.innerHTML}${newLine}`);
  });
  // Replace all breaks with a newline \n
  doc.querySelectorAll('br').forEach(br => {
    br.replaceWith(`${newLine}`);
  });

  // Return the modified document
  return doc.body.innerHTML;
}

/**
 * Removes any HTML tags from a string
 * @param {string} str          The html string to check
 * @returns {string}            The resulting stripped string
 */
function stripHTML(str) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');

  // Replace all pre-code tags with text and a space
  doc.querySelectorAll('pre code').forEach(code => {
    code.parentElement.replaceWith(`${code.textContent} `);
  });

  // Replace all paragraphs with their text content and, if there is some content, a space
  doc.querySelectorAll('p').forEach(el => {
    const { textContent } = el;
    const sp = textContent ? ' ' : '';
    el.replaceWith(`${textContent}${sp}`);
  });

  return doc.body.textContent || '';
}

/**
 * Removes the <pre><code></code></pre> tags from a string
 * @param {string} str  The html code string to check
 * @returns {string}    The resulting stripped string
 */
function stripCode(str) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');

  doc.querySelectorAll('pre code').forEach(code => {
    code.parentElement.replaceWith(code.innerHTML);
  });

  return doc.body.innerHTML || '';
}

/**
 * Returns whether there is one or more case insensitive matches of pattern in the str (can be html)
 * @param   {string}  pattern
 * @param   {string}  str
 * @returns {boolean}
 */
function matchesHTML(pattern, str) {
  if (!pattern || !str) return false;
  return stripHTML(str).toLowerCase().includes(pattern.toLowerCase());
}

/**
 * Returns the code tags content (unescaped html) found in a string in an array
 * @param {string} str      The html string to parse
 * @returns {array}         The resulting code content(s)
 */
function getScripts(str) {
  if (!str) return [];
  const doc = new DomParser().parseFromString(str, 'text/html');
  return Array.from(doc.getElementsByTagName('code')).map(el =>
    _.unescape(el.innerHTML)
  );
}

/**
 * Returns the text content of str
 * @param   {string} str      The html string to parse
 * @returns {string}          The html string without the html tags
 */
function getTextContent(str) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');
  const body = doc.querySelector('body');
  return body.textContent;
}

/**
 * Returns the str without the specific html tag
 * @param   {string} str      The html string to parse
 * @returns {string}          The html string without the elements
 */
function removeCodeElements(str) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');
  doc.querySelectorAll('pre code').forEach(el => el.parentNode.remove());
  return doc.body.innerHTML;
}

/**
 * Returns the str with the code elements replaced with the supplied html.
 * The replacement is enclosed in a paragraph element.
 * @param   {string} str        The html string to parse
 * @param   {array}  replaces   The html strings to replace the code elements
 * @returns {string}            The html string with code elements replaced
 */
function replaceCodeElements(str, replaces) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');
  doc.querySelectorAll('pre code').forEach((el, index) => {
    const paragraph = document.createElement('p');
    paragraph.innerHTML = replaces[index];
    el.parentNode.replaceWith(paragraph);
  });
  return doc.body.innerHTML;
}

/**
 * Returns the str without any enclosing paragraph tag
 * @param {string} str        The string that may be in paragraph e.g. <p>foo</p>
 * @returns {string}          The string without the enclosing paragraph e.g. foo
 */
function removeEnclosingParagraph(str) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');
  const paragraph = doc.querySelector('body p');
  if (paragraph) return paragraph.innerHTML;
  return str;
}

/**
 * Joins in one paragraph the last paragraph of str1 with the first paragraph or str2
 * @param {string} str1 Requires str1 to have at least one hmtl paragraph
 * @param {string} str2 Requires str2 to have at least one html paragraph
 * @returns {string}
 */
function joinParagraphs(str1, str2) {
  if (!str1) return str2;
  if (!str2) return str1;
  const doc1 = new DomParser().parseFromString(str1, 'text/html');
  const paragraphs = doc1.querySelectorAll('body p');
  const lastParagraph = paragraphs[paragraphs.length - 1];
  const doc2 = new DomParser().parseFromString(str2, 'text/html');
  const firstParagraph = doc2.querySelector('body p');
  if (lastParagraph.innerHTML) {
    lastParagraph.innerHTML += ' ';
  }
  lastParagraph.innerHTML += firstParagraph.innerHTML;
  firstParagraph.remove();
  const result = doc1.body.innerHTML + doc2.body.innerHTML;
  return result;
}

/**
 * Clears the style attribute for all blocks (paragraphs, blockquotes) in the given string
 * @param {string} str
 * @returns {string}
 */
function clearBlockStyle(str) {
  if (!str) return str;
  const doc = new DomParser().parseFromString(str, 'text/html');
  const paragraphs = doc.querySelectorAll('body p, body blockquote');
  paragraphs.forEach(paragraph => paragraph.removeAttribute('style'));
  return doc.body.innerHTML;
}

/**
 * Checks if the content inside the editor is non-empty
 * @param {string} str
 */
function isEmpty(str) {
  if (!str) return true;
  const doc = new DomParser().parseFromString(str, 'text/html');
  const nonParagraphs = doc.querySelector('body :not(p)');
  if (nonParagraphs) return false;
  const paragraphs = doc.querySelectorAll('body p');
  if (paragraphs.length === 0 || paragraphs.length > 1) return false;
  if (paragraphs[0].innerHTML) return false;
  return true;
}

/**
 * Truncates str if it exceeds the given length and adds ellipsis (...) at the end
 * @param {string} str        The long string
 * @param {Number} maxLength  The maximum length allowed
 * @returns {string}          The truncated string
 */
function truncateString(str, maxLength = 30) {
  if (!str) return str;
  const truncStr = str.substring(0, maxLength);
  return str.length > maxLength + 4 ? `${truncStr}...` : str;
}

/**
 * Returns the number of words in a string (strips any Html tags)
 * @param {string} str          The string to check
 * @returns {number}            The resulting number of words in the string
 */
function countWords(str) {
  if (!str) return 0;
  const withoutCodeStr = removeCodeElements(str);

  // Add a space after each Paragraph
  const doc = new DomParser().parseFromString(withoutCodeStr, 'text/html');
  const paragraphs = doc.querySelectorAll('body p');
  paragraphs.forEach(p => {
    p.textContent += ' ';
  });
  const strippedStr = doc.body.textContent.trim() || '';

  if (!strippedStr || strippedStr === '') return 0;

  // Clean string
  const cleanString = strippedStr
    .replace(/[^\p{L}\s]|_/gu, '')
    .replace(/\s+/g, ' ');
  const matchedWords = cleanString.match(/\S+/g);
  return matchedWords ? matchedWords.length : 0;
}

/**
 * Formats a number of bytes to KB, MB etc according to its size
 * @param {number} bytes        The number of bytes to format
 * @param {number=} decimals    The decimal points to keep
 * @returns {string}            The resulting size in a more appropriate form
 */
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Converts a number in bytes to the given multiple
 * @param {number}  bytes     The number in bytes to convert
 * @param {string}  multiple  The multiple e.g.: 'KB', 'MB' e.t.c
 * @param {number=}  decimals  The decimal points to keep
 * @returns {number}          The result in the specific multiple
 */
function convertBytes(bytes, multiple, decimals = 2) {
  const dm = decimals < 0 ? 0 : decimals;
  const i = sizes.findIndex(size => size === multiple);
  return parseFloat((bytes / k ** i).toFixed(dm));
}

/**
 * Rounds a number to the nearest integer that is a multiple of the given number
 * @param {number}    number    The number to round
 * @param {number}    multiple  The multiple
 * @returns {number}            The rounded number
 */
function roundToNearestMultiple(number, multiple) {
  const remainder = number % multiple;
  // Round down first
  let rounded = number - remainder;
  // See if we need to round up
  if (remainder >= multiple / 2) {
    rounded += multiple;
  }

  return rounded;
}

/**
 * A shorthand for checking if an object has a property
 * @param   {Object} object
 * @param   {string} property
 * @return  {boolean}
 */
function has(object, property) {
  return Object.prototype.hasOwnProperty.call(object, property);
}

/**
 * Loads the given url and returns an Image object in a resolved promise
 * @param   {string}  url    The image url
 * @return  {Promise}
 */
async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    // image.onerror = reject;
    image.src = url;
    image
      .decode()
      .then(() => resolve(image))
      .catch(() => reject);
  });
}

/**
 * Capitalize the first letter of a word or phrase
 * @param   {string}  str
 * @return  {string}
 */
function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert a string from kebab-case to Title Case
 * @param   {string} str
 * @return  {string}
 */
function kebabCaseToTitleCase(str) {
  if (!str) return str;
  return str
    .toLowerCase()
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
}

/**
 * Convert a path name by adding a suffix to the file name. The file extension remains the same.
 * @param   {string} path
 * @param   {string} suffix
 * @return  {string}
 */
function addFileSufix(path, suffix) {
  const indexOfdot = path.lastIndexOf('.');
  const name = path.substr(0, indexOfdot);
  const extension = path.substr(indexOfdot);
  return `${name}${suffix}${extension}`;
}

/**
 * Returns a string containing the first match of the pattern without exceeding maxsize and choosing whole words.
 * @param {String}  str      The string to search the pattern into
 * @param {String}  pattern  The pattern to look for in the str (case insensitive match)
 * @param {String}  maxsize  Truncate the final string if maxsize is reached
 * @param {boolean} nullNotFound  Will force the function to return null if not found
 * @returns {String}
 */
function findString(str, pattern, maxsize, nullNotFound = false) {
  if (!str || !pattern) return null;

  // Find where the pattern starts inside the string
  const matchPos = str.toLowerCase().search(pattern.toLowerCase());
  // If not found return at most maxsize characters from the beginning
  if (matchPos === -1) {
    return nullNotFound ? null : str.substring(0, maxsize);
  }

  // This is where we start to get the characters
  let leftPosition = matchPos;
  let rightPosition = Math.min(matchPos + maxsize, str.length - 1);

  if (pattern.length < maxsize) {
    const leftDistance = Math.round((maxsize - pattern.length) / 2, 0);
    leftPosition = Math.max(matchPos - leftDistance, 0);

    // If there are no words in the string just return the full substring containing the pattern
    const fullString = str.substring(leftPosition, leftPosition + maxsize);
    if (!fullString.includes(' ')) return fullString;

    // Move to the right until you find the start of a word
    if (leftPosition > 0) {
      while (
        str.charAt(leftPosition - 1) !== ' ' &&
        leftPosition < rightPosition &&
        leftPosition < matchPos
      ) {
        leftPosition += 1;
      }
    }
    // Move to the left until you find the end of a word
    rightPosition = leftPosition + maxsize;
    if (rightPosition < str.length) {
      while (
        str.charAt(rightPosition) !== ' ' &&
        leftPosition < rightPosition &&
        matchPos + pattern.length < rightPosition
      ) {
        rightPosition -= 1;
      }
    }
  }
  return str.substring(leftPosition, rightPosition);
}

/**
 * Marks all occurences of a pattern in a string with <b> html tags (case insensitive)
 * @param {String} str
 * @param {String} pattern
 * @returns {String}
 */
function markString(str, pattern) {
  if (!str || !pattern) return str;
  return str.replace(new RegExp(pattern, 'ig'), '<span>$&</span>');
}

/**
 * Gets the extension of a filename
 * @param   {String} file
 * @returns {String}
 */
function getFileExtension(file) {
  if (!file) return null;
  return file.split('.').pop();
}

/**
 * Checks to see if a filename is allowed in windows.
 * Taken from https://stackoverflow.com/questions/11100821/javascript-regex-for-validating-filenames
 * @param {String} fname
 * @returns {Boolean}
 */
function isValidWindowsFilename(fname) {
  const rg1 = /^[^\\/:*?"<>|]+$/; // forbidden characters \ / : * ? " < > |
  const rg2 = /^\./; // cannot start with dot (.)
  const rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names

  return rg1.test(fname) && !rg2.test(fname) && !rg3.test(fname);
}

/**
 * Checks if a string is a valid email address
 * @param   {String} email
 * @returns {Boolean}
 */
function isValidEmail(email) {
  if (!email) return false;
  const mailformat =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return email.match(mailformat);
}

/**
 * Applies a prefix to all object keys
 * @param   {Object}  obj
 * @param   {String}  prefix
 * @returns {Object}          The resulting object
 */
function addPrefixToObjectKeys(obj, prefix) {
  const result = {};
  Object.keys(obj).forEach(key => {
    result[`${prefix}${key}`] = obj[key];
  });
  return result;
}

/**
 * Searches an array in reverse order and satisfies the predicate
 * @param {array}     arr
 * @param {Function}  predicate
 */
function findLastIndexOf(arr, predicate) {
  let l = arr.length;
  while (l > 0) {
    l -= 1;
    if (predicate(arr[l], l, arr)) return l;
  }
  return -1;
}

/**
 * Converts a numeric elapsed time to a string description
 * @param {Number} elapsedTime
 */
function elapsedTimeToString(elapsedTime) {
  if (elapsedTime >= 0 && elapsedTime <= 10) {
    return 'just now';
  }
  // Less than a minute, show seconds
  if (elapsedTime <= 60) {
    return 'a few seconds ago';
  }

  const getMessage = (time, divider, word) => {
    const num = Math.floor(time / divider);
    const s = num > 1 ? 's' : '';
    return `${num} ${word}${s} ago`;
  };

  // Less than an hour, show minutes
  if (elapsedTime <= 60 * 60) {
    return getMessage(elapsedTime, 60, 'minute');
  }
  // Less than a day, show hours
  if (elapsedTime <= 60 * 60 * 24) {
    return getMessage(elapsedTime, 60 * 60, 'hour');
  }
  // Less than a month, show days
  if (elapsedTime <= 60 * 60 * 24 * 30) {
    return getMessage(elapsedTime, 60 * 60 * 24, 'day');
  }
  // Less than a year, show months
  if (elapsedTime <= 60 * 60 * 24 * 30 * 12) {
    return getMessage(elapsedTime, 60 * 60 * 24 * 30, 'month');
  }
  // More than a year, show years
  return getMessage(elapsedTime, 60 * 60 * 24 * 30 * 12, 'year');
}

/**
 * Clamp a number
 * @param {Number} num
 * @param {Number} min
 * @param {Number} max
 * @returns {Number}
 */
function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

/**
 * Removes null properties from an object
 * @param {Object} obj
 * @returns {Object}
 */
function removeEmpty(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, v]) => v != null)
  );
}

/**
 * Returns the ID of a video from it's URL
 * @param {string} youtubeUrl
 * @returns {string} The youtube video ID
 */
function getYoutubeVideoID(youtubeUrl) {
  const url = new URL(youtubeUrl);
  const domain1 = ['youtube.com', 'www.youtube.com'];
  const domain2 = ['youtu.be', 'www.youtu.be'];
  if (domain1.includes(url.host)) {
    return url.searchParams.get('v');
  }
  if (domain2.includes(url.host)) {
    return url.pathname.split('/')[1];
  }
  return '';
}

/**
 * Returns the thumbnail URL for a YouTube video
 * @param {String} youtubeUrl
 * @returns {String}
 */
function getYoutubeThumbnail(youtubeUrl) {
  const videoId = getYoutubeVideoID(youtubeUrl);
  return `https://i3.ytimg.com/vi/${videoId}/mqdefault.jpg`;
}

/**
 * Returns a promise with the OEmbed info of the youtube video
 * @param {String} youtubeUrl
 * @returns {Promise}
 */
async function getYoutubeOEmbed(youtubeUrl) {
  return axios.get('https://www.youtube.com/oembed', {
    params: {
      url: youtubeUrl,
    },
  });
}

/**
 * Returns the text provided slugified (without transliteration)
 * @param {String} text The text to slugify
 */
function slugify(text) {
  let result = text.toLowerCase();
  result = result.replace(/[.,/#!$%^&*;:{}=\-`~()\\'"]/g, '');
  result = result.replace(/_/g, ' ');
  result = result.replace(/\s+/g, '_');
  return result;
}

/**
 * Returns the query string for the transform.
 * WARNING: MULTIPLIES WITH SCALE FOR BACKWARDS COMPATIBILITY
 * @param {Object} transform An object containing the current transform
 * @returns {String}
 */
function transformToString(transform) {
  const { x, y, scale } = transform;
  return `scale=${scale.toFixed(PANZOOM.URL_SCALE_DIGITS)}&coords=${
    x * scale
  },${y * scale}`;
}

/**
 * Update the current URL by appending a query
 * @param {string} url The project url
 * @param {string} board The board ID
 * @param {Object} transform The transform { x, y, scale }
 */
function updateURLQuery(url, board, transform) {
  const newUrl = `/app${url}?board=${board}&${transformToString(transform)}`;
  if (window.location.pathname.startsWith('/app/project')) {
    window.history.replaceState({}, '', newUrl);
  }
}

/**
 * Get the board & transform values of the URL query params
 * @returns {Object} An object containing the board & transform from the URL query
 */
function loadTransformFromURLQuery() {
  const params = new URLSearchParams(window.location.search);

  const response = {
    transform: {},
  };
  if (params.has('board')) {
    response.board = params.get('board');
  }
  if (params.has('scale')) {
    response.transform.scale = parseFloat(params.get('scale'));
  }
  if (params.has('coords')) {
    const coords = params.get('coords').split(',');
    response.transform.x = parseFloat(coords[0]);
    response.transform.y = parseFloat(coords[1]);

    if (response.transform.scale) {
      response.transform.x /= response.transform.scale;
      response.transform.y /= response.transform.scale;
    }
  }

  return response;
}

/**
 * Escape the special HTML characters in the HTML string
 * @param {string} htmlStr
 * @returns {string}
 */
function escapeHtml(htmlStr) {
  return htmlStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Helper function to retrieve the box style values of an element
 * @param {HTMLElement}         elem  The element for which we will get the style value
 * @param {string}              ruleName  The name of the css style we want to get the values for
 * @param {CSSStyleDeclaration} style The CSS style of the element.
 */
function getBoxStyle(elem, name, style = window.getComputedStyle(elem)) {
  const suffix = name === 'border' ? 'Width' : '';
  return {
    top: parseFloat(style[`${name}Top${suffix}`]),
    right: parseFloat(style[`${name}Right${suffix}`]),
    bottom: parseFloat(style[`${name}Bottom${suffix}`]),
    left: parseFloat(style[`${name}Left${suffix}`]),
  };
}

/**
 * Helper function to retrieve the dimensions of an element and it's parent
 * @param {HTMLElement} el The element we want to retrieve the dimensions for
 */
function getDimensions(el) {
  const rectElem = el.getBoundingClientRect();
  const rectParent = el.parentNode.getBoundingClientRect();

  return {
    elem: {
      width: rectElem.width,
      height: rectElem.height,
      top: rectElem.top,
      right: rectElem.right,
      bottom: rectElem.bottom,
      left: rectElem.left,
      margin: getBoxStyle(el, 'margin'),
      border: getBoxStyle(el, 'border'),
    },
    parent: {
      width: rectParent.width,
      height: rectParent.height,
      top: rectParent.top,
      right: rectParent.right,
      bottom: rectParent.bottom,
      left: rectParent.left,
      padding: getBoxStyle(el.parentNode, 'padding'),
      border: getBoxStyle(el.parentNode, 'border'),
    },
  };
}

/**
 * Translate the viewport coordinates to the actual coordinates of our board
 * @param {Number} clientX The viewport X
 * @param {Number} clientY The viewport Y
 * @param {HTMLElement} panzoomEl The panzoom element
 * @param {Object} transform The current transform
 * @param {Number} transform.scale The current scale
 * @param {Number} transform.x The current X
 * @param {Number} transform.y The current Y
 * @returns {Object} An object containing the X and Y values
 */
function viewportToCoords({ clientX, clientY, panzoomEl, transform }) {
  const dims = getDimensions(panzoomEl);
  const { x, y, scale } = transform;

  // Location in parent
  clientX =
    clientX -
    dims.parent.left -
    dims.parent.padding.left -
    dims.parent.border.left -
    dims.elem.margin.left;
  clientY =
    clientY -
    dims.parent.top -
    dims.parent.padding.top -
    dims.parent.border.top -
    dims.elem.margin.top;

  // Board Difference because of scale
  const diffHorizontal = (dims.elem.width - dims.elem.width / scale) / 2;
  const diffVertical = (dims.elem.height - dims.elem.height / scale) / 2;

  // Remove the transform and the difference
  clientX -= x * scale - diffHorizontal;
  clientY -= y * scale - diffVertical;

  // Translate the coords to the real scale (1)
  clientX /= scale;
  clientY /= scale;

  return { x: clientX, y: clientY };
}

/**
 * Translates the transform values to board coordinates
 * @param {Number} x              The x value of transform
 * @param {Number} y              The y value of transform
 * @param {Number} scale          The scale of the transform
 * @param {HTMLElement} panzoomEl The panzoom element
 * @returns {Object}  An object containing the { x, y, scale } values
 */
function transformToCoords({ x, y, scale, panzoomEl }) {
  const realWidth = panzoomEl.clientWidth;
  const realHeight = panzoomEl.clientHeight;
  const scaledWidth = realWidth * scale;
  const scaledHeight = realHeight * scale;

  const diffHorizontal = (scaledWidth - realWidth) / 2;
  const diffVertical = (scaledHeight - realWidth) / 2;

  const transformX = diffHorizontal / scale - x;
  const transformY = diffVertical / scale - y;

  return { x: transformX, y: transformY, scale };
}

/**
 * Translates the board coordinates to transform values
 * @param {Number} x              The x coordinate
 * @param {Number} y              The y coordinate
 * @param {Number} scale          The scale of the transform
 * @param {HTMLElement} panzoomEl The panzoom element
 * @returns {Object}  An object containing the { x, y, scale } values
 */
function coordsToTransform({ x, y, scale, panzoomEl }) {
  const realWidth = panzoomEl.clientWidth;
  const realHeight = panzoomEl.clientHeight;
  const scaledWidth = realWidth * scale;
  const scaledHeight = realHeight * scale;
  // Due to scaling the board has an offset from the viewport
  const diffHorizontal = (scaledWidth - realWidth) / 2;
  const diffVertical = (scaledHeight - realHeight) / 2;

  let toX =
    x * scale - // x to scale
    diffHorizontal; // remove offset

  let toY = y * scale - diffVertical;

  toX = -toX / scale;
  toY = -toY / scale;

  return { x: toX, y: toY, scale };
}

/**
 * Translate a transform to focal coordinates (see zoomToPoint from @panzoom/panzoom)
 * @param {Number} x        The current X coordinate
 * @param {Number} y        The current Y coordinate
 * @param {Number} scale    The current scale
 * @param {Number} toX      The target X coordinate
 * @param {Number} toY      The target Y coordinate
 * @param {Number} toScale  The target scale
 * @returns {Object}        An object containing the focal x & y values
 */
function transformToFocal({ x, y, scale, toX, toY, toScale }) {
  return {
    x: ((toX - x) * scale * toScale * toScale) / (scale - toScale),
    y: ((toY - y) * scale * toScale * toScale) / (scale - toScale),
  };
}

/**
 * Find if a certain element is a descendant of another element by class
 * @param {Element}   elem      The descendant element
 * @param {string}    className The ancestor's class
 * @param {Element}   untilElem An element to stop the search
 */
function isDescendantOfClass(elem, className, untilElem) {
  for (let cur = elem; cur != null && cur !== untilElem; cur = cur.parentNode) {
    if (cur.classList.contains(className)) {
      return true;
    }
  }
  return false;
}

export {
  convertBytes,
  formatBytes,
  roundToNearestMultiple,
  loadImage,
  countWords,
  stripHTML,
  stripCode,
  matchesHTML,
  convertHTMLtoBBCode,
  has,
  capitalize,
  kebabCaseToTitleCase,
  addFileSufix,
  findString,
  markString,
  truncateString,
  getFileExtension,
  isValidWindowsFilename,
  isValidEmail,
  addPrefixToObjectKeys,
  getScripts,
  removeCodeElements,
  replaceCodeElements,
  removeEnclosingParagraph,
  joinParagraphs,
  clearBlockStyle,
  isEmpty,
  getTextContent,
  findLastIndexOf,
  elapsedTimeToString,
  clamp,
  removeEmpty,
  getYoutubeVideoID,
  getYoutubeThumbnail,
  getYoutubeOEmbed,
  updateURLQuery,
  loadTransformFromURLQuery,
  slugify,
  escapeHtml,
  getBoxStyle,
  getDimensions,
  viewportToCoords,
  transformToFocal,
  coordsToTransform,
  transformToCoords,
  isDescendantOfClass,
};
